// src/components/books/CustomDomainBookViewer.jsx
import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Share2, ExternalLink } from 'lucide-react';
import { BookViewer } from '../../pages/BookViewer';
import { EnhancedCatalogTemplate } from '../templates/CatalogTemplate';
import { AlbumTemplate } from '../templates/AlbumTemplate';
import { ProfileTemplate } from '../templates/ProfileTemplate';
import { EcommerceTemplate } from '../templates/EcommerceTemplate';
import { InstitutionTemplate } from '../templates/InstitutionTemplate';
import { useAuth } from '../../contexts/AuthContext';

export const CustomDomainBookViewer = ({ bookData, bookId }) => {
  const { user } = useAuth();
  // Apply custom domain specific styling
  useEffect(() => {
    // Apply custom CSS for the domain
    if (bookData?.customCSS) {
      const styleId = 'custom-domain-styles';
      let styleElement = document.getElementById(styleId);
      
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      
      styleElement.textContent = bookData.customCSS;
    }

    // Add custom domain specific body classes
    document.body.classList.add('custom-domain');
    
    // Remove default app styling that might interfere
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    
    return () => {
      // Cleanup on unmount
      const styleElement = document.getElementById('custom-domain-styles');
      if (styleElement) {
        styleElement.remove();
      }
      document.body.classList.remove('custom-domain');
    };
  }, [bookData?.customCSS]);

  // Handle special template types with custom domain optimization
  if (bookData?.templateType === 'catalog') {
    return (
      <CustomDomainWrapper bookData={bookData}>
        <EnhancedCatalogTemplate
          user={user}
          bookId={bookId}
          bookData={bookData}
          styling={{
            primaryColor: bookData?.styling?.primaryColor || bookData?.primaryColor || '#0795ff',
            backgroundColor: bookData?.styling?.backgroundColor || bookData?.backgroundColor || '#f8fafc',
            headerColor: bookData?.styling?.headerColor || bookData?.headerColor || '#ffffff',
            textColor: bookData?.styling?.textColor || '#3b3c26',
            fontFamily: bookData?.fontFamily || 'Helvetica, sans-serif'
          }}
        />
      </CustomDomainWrapper>
    );
  }
  {/*
  if (bookData?.templateType === 'album') {
    return (
      <CustomDomainWrapper bookData={bookData}>
        <AlbumTemplate
          user={user}
          bookId={bookId}
          bookData={bookData}
          customStyling={{
            primaryColor: bookData?.styling?.primaryColor || bookData?.primaryColor || '#190192',
            backgroundColor: bookData?.styling?.backgroundColor || bookData?.backgroundColor || '#f8fafc',
            headerColor: bookData?.styling?.headerColor || bookData?.headerColor || '#ffffff',
            textColor: bookData?.styling?.textColor || '#3b3c26',
            fontFamily: bookData?.fontFamily || 'Helvetica, sans-serif'
          }}
        />
      </CustomDomainWrapper>
    );
  }
    */}

  // For regular interactive books, wrap BookViewer with custom domain styling
  return (
      <CustomDomainWrapper bookData={bookData}>
        <BookViewer 
          user={user}
          bookData={bookData}
          bookId={bookId}
          isCustomDomain={true}
          isPublicBook={true}
          renderMode="custom-domain"
          hideNavigation={false}
        />
      </CustomDomainWrapper>
  );
};

// Wrapper component for custom domain specific layout and styling
const CustomDomainWrapper = ({ bookData, user, children }) => {
  const customDomainStyles = {
    '--book-primary': bookData?.primaryColor || bookData?.styling?.primaryColor || '#2563eb',
    '--book-background': bookData?.backgroundColor || bookData?.styling?.backgroundColor || '#f8fafc',
    '--book-header': bookData?.headerColor || bookData?.styling?.headerColor || '#2563eb',
    '--book-accent': bookData?.accentColor || bookData?.styling?.accentColor || '#8b5cf6',
    fontFamily: bookData?.fontFamily || 'Helvetica, Arial, sans-serif'
  };

  return (
    <div 
      className="custom-domain-container min-h-screen overflow-hidden"
      style={customDomainStyles}
      
    >

      {/* Custom Domain Header (minimal, non-intrusive) */}
      <div className="custom-domain-header">
        {/* Add any custom domain specific header elements here if needed */}
        {/* This could include custom domain branding, special announcements, etc. */}
      </div>

      {/* Main Content - This is where BookViewer renders */}
      <div className="custom-domain-content">
        {children}
      </div>

      {/* Custom Domain Footer 
      <CustomDomainFooter bookData={bookData} />
        
      {/* Custom Domain Specific Styles */}
      <style jsx>{`
        .custom-domain-container {
          /* Override any conflicting app styles */
          position: relative;
          width: 100%;
          min-height: 100vh;
        }

       
        /* Override BookViewer styles for custom domain */
        .custom-domain-content .min-h-screen {
          min-height: 100%;
        }

        /* Custom domain specific overrides */
        .custom-domain-content .bg-gray-50 {
          background-color: var(--book-background) !important;
        }

        /* Ensure proper spacing */
        .custom-domain-content > * {
          margin: 0;
        }

        /* Hide any app-specific elements that shouldn't show on custom domains */
        .custom-domain-content .app-navbar,
        .custom-domain-content .app-breadcrumb {
          display: none !important;
        }

        /* Custom domain responsive adjustments */
        @media (max-width: 768px) {
          .custom-domain-content {
            padding: 0;
          }
        }
      `}</style>
    </div>
  );
};

// Custom domain footer
const CustomDomainFooter = ({ bookData }) => {
  return (
    <footer className="custom-domain-footer bg-white border-t border-gray-200 py-4">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          {/* Left side - Domain info */}
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
              {window.location.hostname}
            </span>
          </div>

          {/* Center - Book info (optional) */}
          <div className="hidden md:flex items-center gap-4 text-xs text-gray-500">
            {bookData?.botCategory && (
              <span>{bookData.botCategory}</span>
            )}
          </div>

          {/* Right side - Powered by */}
          <div className="flex items-center gap-2">
            <span>Powered by</span>
            <a 
              href="https://parrotpages.co.za" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-semibold text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1"
            >
              ParrotPages
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default CustomDomainBookViewer;